import {
  AVAILABLE_COLORS,
  TRACTOR_STALE_THRESHOLD_S,
} from "common/utils/rtcJobs";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { capitalize } from "portal/utils/strings";
import {
  ChecklistExemptionUpdate,
  ChecklistUpdate,
  ObjectiveUpdate,
  UpdateType,
} from "protos/rtc/web_socket";
import { classes } from "portal/theme/theme";
import { DateTime } from "luxon";
import { ExemptionButton } from "common/components/ExemptionButton";
import { getLastUpdatedString } from "portal/utils/dates";
import { jobExplorer } from "common/state/jobExplorer";
import { Link } from "react-router-dom";
import { Objective } from "protos/rtc/jobs";
import { Path } from "portal/utils/routing";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { SafetyChecklistDisplay } from "common/components/SafetyChecklistDisplay";
import { Section } from "portal/components/map/navigation/Section";
import { store } from "portal/state/store";
import { TractorObjectiveQueue } from "./TractorObjectiveQueue";
import { useAppDispatch } from "portal/state/hooks";
import { useLiveRobotJobData } from "common/hooks/useLiveJobData";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/ContentPasteGo";
import CloseIcon from "@mui/icons-material/Close";
import JobIcon from "@mui/icons-material/AssignmentOutlined";
import ObjectiveIcon from "@mui/icons-material/ContentPaste";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

interface TractorDetailsProps {
  tractor: { serial: string };
}

const LAST_UPDATED_THRESHOLD = TRACTOR_STALE_THRESHOLD_S;
const LAST_UPDATED_UPPER_THRESHOLD_M = 10; // 10 minutes

export const TractorDetails: FC<TractorDetailsProps> = ({ tractor }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [mutateAssignment] = useMutationPopups(
    rtcJobsApi.endpoints.setAssignment.useMutation()
  );
  const explorerJob = useSelector(jobExplorer.selectors.selectJob);
  const tractorData = jobExplorer.selectors.selectTractorDataBySerials(
    store.getState(),
    [tractor.serial]
  )[tractor.serial];

  const { data: objectiveAssignments, refetch: refetchObjectiveAssignments } =
    rtcJobsApi.endpoints.listActionableAssignments.useQuery({
      robotSerial: tractor.serial,
    });

  const { data: recommendedObjectives, refetch: refetchRecommendedObjectives } =
    rtcJobsApi.endpoints.listRecommendedObjectives.useQuery(
      {
        robotSerial: tractor.serial,
        jobId: explorerJob?.id ?? 0,
      },
      { skip: !explorerJob }
    );

  const { data: job } = rtcJobsApi.endpoints.getConfiguredJob.useQuery(
    {
      id: explorerJob?.id.toString() ?? "",
    },
    { skip: !explorerJob }
  );

  const relevantObjectiveIds = useMemo(
    () => [
      ...new Set([
        ...(job?.job.objectives ?? []).map((o) => o.id),
        ...(objectiveAssignments?.assignments ?? []).map(
          (oa) => oa.objectiveId
        ),
      ]),
    ],
    [job, objectiveAssignments]
  );
  const { data: objectives } = rtcJobsApi.endpoints.listObjectives.useQuery(
    { ids: relevantObjectiveIds },
    // passing an empty array returns every objective the user can see
    { skip: relevantObjectiveIds.length === 0 }
  );
  const objectivesById = useMemo(
    () =>
      Object.fromEntries(
        (objectives?.objectives ?? []).map((obj) => [obj.id, obj])
      ),
    [objectives?.objectives]
  );

  const relevantJobIds = useMemo(
    () => [...new Set((objectives?.objectives ?? []).map((o) => o.jobId))],
    [objectives]
  );
  const { data: jobs } = rtcJobsApi.endpoints.listJobs.useQuery(
    { ids: relevantJobIds },
    // passing an empty array returns every job the user can see
    { skip: relevantJobIds.length === 0 }
  );
  const jobsById = useMemo(
    () => Object.fromEntries((jobs?.jobs ?? []).map((job) => [job.id, job])),
    [jobs?.jobs]
  );

  const [activeObjective, setActiveObjective] = useState<Objective>();
  const [getActiveObjective] =
    rtcJobsApi.endpoints.getActiveObjective.useLazyQuery();
  // initial fetch for activeObjective
  useEffect(() => {
    const ac = new AbortController();
    const fetch = async (): Promise<void> => {
      const { data } = await getActiveObjective({
        serial: tractor.serial,
      });
      if (ac.signal.aborted) {
        return;
      }
      setActiveObjective(data);
    };
    fetch();
    return () => ac.abort();
  }, [getActiveObjective, tractor.serial]);
  const onActiveObjectiveUpdate = useCallback(
    (o: ObjectiveUpdate) => setActiveObjective(o.objective),
    []
  );
  const onAssignmentUpdate = useCallback(() => {
    refetchObjectiveAssignments();
    refetchRecommendedObjectives();
  }, [refetchObjectiveAssignments, refetchRecommendedObjectives]);
  // checklist and exemption events fill the queries the displays below read
  const fieldId = explorerJob?.fieldId;
  const onChecklistUpdate = useCallback(
    (u: ChecklistUpdate) => {
      const checklist = u.checklist;
      if (!fieldId || checklist?.fieldId !== fieldId) {
        return;
      }
      dispatch(
        rtcJobsApi.util.upsertQueryData(
          "getLatestValidChecklist",
          { robotSerial: tractor.serial, fieldId },
          u.type === UpdateType.UPDATE_TYPE_DELETE
            ? { ...checklist, valid: false }
            : checklist
        )
      );
    },
    [dispatch, fieldId, tractor.serial]
  );
  const onChecklistExemptionUpdate = useCallback(
    (u: ChecklistExemptionUpdate) => {
      const exemption = u.checklistExemption;
      if (!exemption || (fieldId && exemption.fieldId !== fieldId)) {
        return;
      }
      dispatch(
        rtcJobsApi.util.upsertQueryData(
          "getChecklistExemption",
          { serial: tractor.serial, fieldId },
          u.type === UpdateType.UPDATE_TYPE_DELETE ? undefined : exemption
        )
      );
    },
    [dispatch, fieldId, tractor.serial]
  );
  useLiveRobotJobData({
    serial: tractor.serial,
    onActiveObjectiveUpdate,
    onAssignmentUpdate,
    onChecklistUpdate,
    onChecklistExemptionUpdate,
  });

  const [now, setNow] = useState<DateTime>(DateTime.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(DateTime.now());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const [unassigningActiveObjective, setUnassigningActiveObjective] =
    useState<boolean>(false);

  const lastUpdatedSeconds = tractorData?.liveTractorData?.timestamp
    ? now.toSeconds() -
      DateTime.fromISO(tractorData.liveTractorData.timestamp).toSeconds()
    : Number.POSITIVE_INFINITY;

  const lastUpdatedString = getLastUpdatedString({
    lastUpdatedSeconds,
    lastUpdatedThreshold: LAST_UPDATED_THRESHOLD,
    lastUpdatedUpperThreshold: LAST_UPDATED_UPPER_THRESHOLD_M,
    t,
  });

  const [deleteActiveObjective] = useMutationPopups(
    rtcJobsApi.endpoints.deleteActiveObjective.useMutation()
  );
  const [removeAssignment] = useMutationPopups(
    rtcJobsApi.endpoints.removeAssignment.useMutation()
  );

  const handleUnassignment = (): void => {
    if (!tractor.serial) {
      return;
    }
    deleteActiveObjective({ serial: tractor.serial }).then(() => {
      if (activeObjective?.id) {
        removeAssignment({ objectiveId: activeObjective.id }).then(() => {
          setUnassigningActiveObjective(false);
        });
      }
    });
  };

  return (
    <div className="h-full">
      <LinearProgress
        variant={tractorData?.tractorIsMoving ? "indeterminate" : "determinate"}
        value={100}
        color={tractorData?.tractorIsStale ? "secondary" : "success"}
      />
      <div className="flex flex-col p-4 gap-1">
        <div className="flex justify-between">
          <Typography
            variant="h6"
            className={classes(
              "flex my-auto",
              tractorData?.tractorIsStale ? "text-slate-500" : "text-green-500"
            )}
          >
            {tractorData?.tractorIsStale
              ? t("utils.descriptors.inactive")
              : t("utils.descriptors.active")}
          </Typography>
          <Typography className="text-slate-500 text-sm flex my-auto justify-end">
            {lastUpdatedString}
          </Typography>
        </div>
        <div className="flex gap-4 justify-between">
          <Select
            className="w-fit"
            value={tractorData?.color}
            onChange={(e) =>
              dispatch(
                jobExplorer.actions.setTractorColor({
                  serial: tractor.serial,
                  color: e.target.value,
                })
              )
            }
          >
            {AVAILABLE_COLORS.map((c) => (
              <MenuItem key={c} value={c}>
                <div className="flex w-5 h-5" style={{ backgroundColor: c }} />
              </MenuItem>
            ))}
          </Select>
          <div className="flex flex-col my-auto">
            {explorerJob?.fieldId && (
              <SafetyChecklistDisplay
                serial={tractor.serial}
                fieldId={explorerJob.fieldId}
              />
            )}
            <ExemptionButton
              serial={tractor.serial}
              fieldId={explorerJob?.fieldId}
            />
          </div>
        </div>
      </div>
      <Divider />
      <div className="flex gap-2 p-4">
        <ObjectiveIcon color="info" className="flex my-auto" />
        <Typography
          className={classes(
            "flex my-auto",
            !activeObjective && "text-slate-500"
          )}
        >
          {activeObjective?.name ?? capitalize(t("utils.descriptors.none"))}
        </Typography>
        {activeObjective && activeObjective.jobId !== job?.job.id && (
          <Link
            target="_blank"
            to={`${Path.JOBS}/${activeObjective.jobId}/orchestrate`}
            className="flex my-auto gap-1"
          >
            <JobIcon color="warning" className="flex my-auto" />
            <Typography>{activeObjective.jobId}</Typography>
          </Link>
        )}
        {activeObjective && (
          <IconButton
            color="inherit"
            className="flex my-auto ml-auto"
            onClick={() => setUnassigningActiveObjective(true)}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Dialog open={unassigningActiveObjective}>
          <DialogTitle>{t("components.ConfirmationDialog.title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("views.autotractor.unassignDialog.body")}
            </DialogContentText>
            <DialogActions>
              <Button
                variant="contained"
                color="info"
                onClick={() => setUnassigningActiveObjective(false)}
              >
                {t("utils.actions.cancel")}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleUnassignment()}
              >
                {t("utils.actions.confirm")}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </div>
      <Divider />
      <div className="h-full overflow-y-auto">
        <Section
          header={
            <div className="flex my-auto gap-2">
              <AssignmentIcon color="info" className="flex my-auto" />
              <Typography className="flex my-auto">
                {t("models.autotractor.assignment_other")}
              </Typography>
            </div>
          }
        >
          <div className="">
            <TractorObjectiveQueue
              tractor={
                tractorData?.liveTractorData?.data
                  ? {
                      ...tractorData.liveTractorData.data,
                      serial: tractor.serial,
                    }
                  : undefined
              }
              jobsById={jobsById}
              objectivesById={objectivesById}
              activeObjectiveId={activeObjective?.id}
              objectiveAssignments={objectiveAssignments?.assignments ?? []}
            />
          </div>
        </Section>
        <Section
          header={
            <Typography>
              {t("views.autotractor.suggestedAssignments")}
            </Typography>
          }
        >
          <div className="flex flex-col overflow-y-auto">
            {(recommendedObjectives?.objectives ?? []).map((o) => (
              <div key={o.id}>
                <Button
                  className={classes(
                    "hover:bg-slate-600",
                    "flex gap-2 mx-auto p-2 cursor-pointer"
                  )}
                  color="info"
                  fullWidth
                  onClick={() =>
                    mutateAssignment({
                      objectiveId: o.id,
                      robotSerial: tractor.serial,
                    })
                  }
                >
                  <AddIcon color="info" />
                  <AssignmentIcon className="flex my-auto" />
                  <Typography className="flex my-auto">{o.name}</Typography>
                </Button>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};
